import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Check, X, Pause, Play, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const STATUS_LABEL = { pending: 'In attesa', published: 'Pubblicato', rejected: 'Rifiutato', suspended: 'Sospeso' };
const STATUS_TONE = { pending: 'bg-terracotta/20 text-terracotta', published: 'bg-accent text-accent-foreground', rejected: 'bg-destructive/20 text-destructive', suspended: 'bg-secondary' };

export default function AdminListings({ defaultStatus }) {
  const [sp, setSp] = useSearchParams();
  const status = sp.get('status') || defaultStatus || '';
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [action, setAction] = useState(null); // {kind, listing}
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    const { data } = await api.get('/admin/listings', { params: { status: status || undefined, q: q || undefined } });
    setItems(data || []);
  }, [status, q]);
  useEffect(() => { load(); }, [load]);

  const doAction = async () => {
    if (!action) return;
    const { kind, listing } = action;
    try {
      await api.post(`/admin/listings/${listing.listing_id}/${kind}`, { reason: reason || null });
      toast.success(`Terreno ${kind === 'approve' ? 'ripubblicato' : kind === 'reject' ? 'rifiutato' : kind === 'suspend' ? 'sospeso' : 'riattivato'}`);
      setAction(null); setReason('');
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = (s) => setSp(s ? { status: s } : {});

  return (
    <div className="space-y-6" data-testid="admin-listings">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta">Terreni</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tighter mt-2">Tutti i terreni</h1>
          <p className="mt-2 text-sm text-muted-foreground">I nuovi annunci sono pubblicati automaticamente. Qui puoi sospendere, rifiutare o riattivare gli annunci: ogni azione è tracciata nell'Audit Log e il proprietario riceve notifica.</p>
        </div>
      </header>

      <div className="rounded-2xl border border-border bg-card p-4 flex flex-wrap gap-3">
        <Input placeholder="Cerca titolo o proprietario" value={q} onChange={e=>setQ(e.target.value)} className="max-w-sm"/>
        {!defaultStatus && (
          <Select value={status || 'all'} onValueChange={v=>setStatus(v==='all'?'':v)}>
            <SelectTrigger className="w-[200px]" data-testid="admin-listings-status-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="published">Pubblicati</SelectItem>
              <SelectItem value="rejected">Rifiutati</SelectItem>
              <SelectItem value="suspended">Sospesi</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        {items.length===0 ? <div className="p-10 text-sm text-muted-foreground text-center">Nessun terreno da mostrare</div> :
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/60">
                <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <th className="px-4 py-3">Titolo</th>
                  <th className="px-4 py-3">Proprietario</th>
                  <th className="px-4 py-3">Località</th>
                  <th className="px-4 py-3">Sup.</th>
                  <th className="px-4 py-3">Prezzo</th>
                  <th className="px-4 py-3">Tipo</th>
                  <th className="px-4 py-3">Stato</th>
                  <th className="px-4 py-3">Data</th>
                  <th className="px-4 py-3 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map(l => (
                  <tr key={l.listing_id} data-testid={`admin-listing-row-${l.listing_id}`}>
                    <td className="px-4 py-3">
                      <Link className="font-medium hover:underline flex items-center gap-1" to={`/terreno/${l.listing_id}`} target="_blank">
                        {l.title} <ExternalLink size={12}/>
                      </Link>
                    </td>
                    <td className="px-4 py-3">{l.owner_name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{[l.location?.comune, l.location?.region].filter(Boolean).join(', ')}</td>
                    <td className="px-4 py-3">{l.area_hectares} ha</td>
                    <td className="px-4 py-3">{l.price ? `€ ${new Intl.NumberFormat('it-IT').format(l.price)}` : '—'}</td>
                    <td className="px-4 py-3">{l.listing_type==='rent'?'Affitto':'Vendita'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${STATUS_TONE[l.status]||'bg-secondary'}`}>{STATUS_LABEL[l.status]||l.status}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(l.created_at).toLocaleDateString('it-IT')}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        {l.status !== 'published' && <Button size="sm" variant="outline" onClick={()=>setAction({kind:'approve', listing:l})} data-testid={`admin-approve-${l.listing_id}`}><Check size={12} className="mr-1"/> Ripubblica</Button>}
                        {l.status !== 'rejected' && <Button size="sm" variant="outline" onClick={()=>setAction({kind:'reject', listing:l})} data-testid={`admin-reject-${l.listing_id}`}><X size={12} className="mr-1"/> Rifiuta</Button>}
                        {l.status === 'published' && <Button size="sm" variant="outline" onClick={()=>setAction({kind:'suspend', listing:l})}><Pause size={12} className="mr-1"/> Sospendi</Button>}
                        {l.status === 'suspended' && <Button size="sm" variant="outline" onClick={()=>setAction({kind:'reactivate', listing:l})}><Play size={12} className="mr-1"/> Riattiva</Button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </div>

      <Dialog open={!!action} onOpenChange={o=>{ if(!o){setAction(null); setReason('');} }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Motivazione ({action?.kind})</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground">Annuncio: <b>{action?.listing?.title}</b></div>
          <Textarea rows={3} placeholder="Motivo (opzionale ma consigliato per audit log)" value={reason} onChange={e=>setReason(e.target.value)}/>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>{setAction(null); setReason('');}}>Annulla</Button>
            <Button onClick={doAction}>Conferma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
